import React, { useMemo, useState, useEffect } from 'react';
import {
  Plus,
  Upload,
  ChevronRight,
  ChevronDown,
  Folder,
  FolderOpen,
  FilePlus,
  FolderPlus,
  Trash2,
  DatabaseBackup,
  PanelLeftClose,
  Eraser,
} from 'lucide-react';
import { Button } from './Button';
import { Selectable } from 'kysely';
import { SessionFiles } from '../helpers/schema';
import {
  useDeleteFile,
  useUpdateFile,
  useCreateFile,
  useCleanupDuplicates,
} from '../helpers/useFilesApi';
import { useCheckpoints } from '../helpers/useCheckpointsApi';
import { toast } from 'sonner';
import { FileTreeItem, FileStatus } from './FileTreeItem';
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
} from './ContextMenu';
import styles from './FileTree.module.css';
import { CreateFileDialog } from './CreateFileDialog';

interface FileTreeProps {
  files: Selectable<SessionFiles>[];
  activeFileId: string | null;
  onSelectFile: (fileId: string) => void;
  onAddFile: () => void;
  onImport?: () => void;
  // New: allow showing a download button (session owner only)
  sessionId?: string;
  isOwner?: boolean;
  // Collapse/expand toggle
  onCollapse?: () => void;
}

export const FileTree = ({
  files,
  activeFileId,
  onSelectFile,
  onAddFile,
  onImport,
  sessionId = '',
  isOwner = false,
  onCollapse,
}: FileTreeProps) => {
  // Defensive local fallback in case `sessionId` is missing in some bundles
  const safeSessionId = typeof sessionId !== 'undefined' && sessionId ? sessionId : '';
  const deleteFile = useDeleteFile();
  const updateFile = useUpdateFile();
  const createFile = useCreateFile();
  const cleanupDuplicates = useCleanupDuplicates();
  const [isCleaningUp, setIsCleaningUp] = useState(false);

  // Handler to cleanup duplicate files
  const handleCleanupDuplicates = async () => {
    if (!safeSessionId || isCleaningUp) return;
    setIsCleaningUp(true);
    try {
      const result = await cleanupDuplicates.mutateAsync(safeSessionId);
      if (result.removed > 0) {
        toast.success(`Removed ${result.removed} duplicate file${result.removed !== 1 ? 's' : ''}`);
      } else {
        toast.info('No duplicate files found');
      }
    } catch (err) {
      toast.error('Failed to cleanup duplicates');
    } finally {
      setIsCleaningUp(false);
    }
  };

  // Get checkpoints to compare file states
  const { data: checkpointsData } = useCheckpoints(safeSessionId || undefined);

  // Calculate file status by comparing with latest checkpoint
  const fileStatusMap = useMemo(() => {
    const statusMap = new Map<string, FileStatus>();

    if (!checkpointsData?.checkpoints || checkpointsData.checkpoints.length === 0) {
      // No checkpoint, mark all files as added
      files.forEach((file) => {
        const key = `${file.filePath}/${file.fileName}`;
        statusMap.set(key, 'added');
      });
      return statusMap;
    }

    // Get the latest checkpoint
    const latestCheckpoint = checkpointsData.checkpoints[0];
    let checkpointFiles: any[] = [];

    try {
      if (typeof latestCheckpoint.filesSnapshot === 'string') {
        checkpointFiles = JSON.parse(latestCheckpoint.filesSnapshot);
      } else if (Array.isArray(latestCheckpoint.filesSnapshot)) {
        checkpointFiles = latestCheckpoint.filesSnapshot;
      }
    } catch (e) {
      console.error('Failed to parse checkpoint files:', e);
    }

    // Create a map of checkpoint files by path
    const checkpointFileMap = new Map<string, any>();
    checkpointFiles.forEach((f: any) => {
      // Normalize paths for matching
      const normalizePath = (path: string) => {
        if (!path || path === '/') return '/';
        return path.replace(/\/+/g, '/').replace(/\/$/, '') || '/';
      };

      const filePath = normalizePath(f.filePath || '/');
      // Store multiple key formats for flexible matching
      const keys = [
        `${filePath}/${f.fileName}`,
        `${filePath}${f.fileName}`,
        `/${f.fileName}`,
        f.fileName,
      ];

      keys.forEach((key) => {
        if (!checkpointFileMap.has(key)) {
          checkpointFileMap.set(key, f);
        }
      });
    });

    // Compare current files with checkpoint
    files.forEach((file) => {
      // Normalize paths for comparison
      const normalizePath = (path: string) => {
        if (!path || path === '/') return '/';
        return path.replace(/\/+/g, '/').replace(/\/$/, '') || '/';
      };

      const filePath = normalizePath(file.filePath || '/');
      const key = `${filePath}/${file.fileName}`;

      // Try multiple key formats for matching
      const possibleKeys = [key, `${filePath}${file.fileName}`, `/${file.fileName}`, file.fileName];

      let checkpointFile: any = null;
      for (const possibleKey of possibleKeys) {
        checkpointFile = checkpointFileMap.get(possibleKey);
        if (checkpointFile) break;
      }

      if (!checkpointFile) {
        // File doesn't exist in checkpoint - it's new
        statusMap.set(key, 'added');
      } else if (checkpointFile.content !== file.content) {
        // Content changed - it's modified
        statusMap.set(key, 'modified');
      } else {
        // No change
        statusMap.set(key, 'unchanged');
      }
    });

    return statusMap;
  }, [files, checkpointsData]);

  const handleDelete = (fileId: string) => {
    const file = files.find((f) => f.id === fileId);
    if (!file) return;

    if (confirm(`Delete ${file.fileName}?`)) {
      deleteFile.mutate(
        { id: fileId },
        {
          onSuccess: async () => {
            toast.success('File deleted');
          },
          onError: () => toast.error('Failed to delete file'),
        },
      );
    }
  };

  const handleRename = (fileId: string, newName: string) => {
    const file = files.find((f) => f.id === fileId);
    if (!file) return;

    updateFile.mutate(
      {
        id: fileId,
        fileName: newName,
      },
      {
        onSuccess: async () => {
          toast.success('File renamed');
        },
        onError: () => toast.error('Failed to rename file'),
      },
    );
  };

  const handleDuplicate = (file: Selectable<SessionFiles>) => {
    // Construct new filename: "filename copy.ext" or "filename copy"
    const parts = file.fileName.split('.');
    let newName = '';
    if (parts.length > 1) {
      const ext = parts.pop();
      newName = `${parts.join('.')} copy.${ext}`;
    } else {
      newName = `${file.fileName} copy`;
    }

    // Construct new filePath as well
    const pathParts = file.filePath.split('/');
    pathParts[pathParts.length - 1] = newName;
    const newFilePath = pathParts.join('/');

    createFile.mutate(
      {
        sessionId: file.sessionId,
        fileName: newName,
        filePath: newFilePath,
        content: file.content,
        language: file.language,
      },
      {
        onSuccess: async () => {
          toast.success('File duplicated');
        },
        onError: () => toast.error('Failed to duplicate file'),
      },
    );
  };

  // Build folder tree structure from files
  type FolderNode = {
    name: string;
    path: string;
    files: Selectable<SessionFiles>[];
    children: Map<string, FolderNode>;
  };

  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set(['/']));
  const [createFileInFolder, setCreateFileInFolder] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);

  // Auto-expand folders to show active file
  useEffect(() => {
    if (activeFileId) {
      const activeFile = files.find((f) => f.id === activeFileId);
      if (activeFile) {
        const filePath = activeFile.filePath || '/';
        // Expand all parent folders
        const pathParts = filePath === '/' ? [] : filePath.split('/').filter(Boolean);
        const pathsToExpand = new Set<string>(['/']);
        let currentPath = '';
        for (const part of pathParts) {
          currentPath = currentPath === '' ? `/${part}/` : `${currentPath}${part}/`;
          pathsToExpand.add(currentPath);
        }
        setExpandedFolders((prev) => new Set([...prev, ...pathsToExpand]));
      }
    }
  }, [activeFileId, files]);

  const folderTree = useMemo(() => {
    const root: FolderNode = {
      name: '',
      path: '/',
      files: [],
      children: new Map(),
    };

    files.forEach((file) => {
      // filePath is the directory path (e.g., "/scenes/effects/")
      // fileName is just the filename (e.g., "hit_effect.tscn")
      let filePath = file.filePath || '/';

      // Normalize: ensure it starts with / and ends with /
      if (!filePath.startsWith('/')) filePath = '/' + filePath;
      if (filePath !== '/' && !filePath.endsWith('/')) {
        filePath = filePath + '/';
      }

      // Build folder structure from the path
      const pathParts = filePath === '/' ? [] : filePath.split('/').filter(Boolean);
      let current = root;

      // Navigate/create folder structure
      for (const part of pathParts) {
        if (!current.children.has(part)) {
          const folderPath = current.path === '/' ? `/${part}/` : `${current.path}${part}/`;
          current.children.set(part, {
            name: part,
            path: folderPath,
            files: [],
            children: new Map(),
          });
        }
        current = current.children.get(part)!;
      }

      // Add file to current folder
      current.files.push(file);
    });

    return root;
  }, [files]);

  const toggleFolder = (path: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  const handleCreateFileInFolder = (folderPath: string) => {
    setCreateFileInFolder(folderPath);
  };

  const handleCreateFolder = async (parentPath: string) => {
    const folderName = prompt('Folder name:');
    if (!folderName || !folderName.trim()) return;

    // Create a placeholder file to represent the folder (or we could add folder support to DB)
    // For now, create a .gitkeep file
    const folderPath = parentPath === '/' ? `/${folderName}/` : `${parentPath}${folderName}/`;
    try {
      await createFile.mutateAsync({
        sessionId: safeSessionId,
        fileName: '.gitkeep',
        filePath: folderPath,
        content: '',
        language: 'plaintext',
      });
      toast.success(`Folder "${folderName}" created`);
      setExpandedFolders((prev) => new Set([...prev, folderPath]));
    } catch (err) {
      toast.error('Failed to create folder');
    }
  };

  const handleDeleteFolder = async (folderPath: string) => {
    // Find all files in this folder and subfolders
    const filesInFolder = files.filter((f) => {
      const filePath = f.filePath || '/';
      return filePath.startsWith(folderPath) || filePath === folderPath;
    });

    if (filesInFolder.length === 0) {
      toast.info('Folder is already empty');
      return;
    }

    if (!confirm(`Delete folder and ${filesInFolder.length} file(s)?`)) return;

    // Delete all files in folder
    for (const file of filesInFolder) {
      try {
        await deleteFile.mutateAsync({ id: file.id });
      } catch (err) {
        console.error('Failed to delete file:', err);
      }
    }

    toast.success('Folder deleted');
  };

  const renderFolder = (
    folder: FolderNode,
    depth: number = 0,
    isLast: boolean = true,
  ): React.ReactNode => {
    const isExpanded = expandedFolders.has(folder.path);
    const hasChildren = folder.children.size > 0 || folder.files.length > 0;
    const FolderIcon = isExpanded ? FolderOpen : Folder;
    const ChevronIcon = isExpanded ? ChevronDown : ChevronRight;

    // Get sorted children for rendering
    const sortedChildren = Array.from(folder.children.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
    const sortedFiles = folder.files.sort((a, b) => a.fileName.localeCompare(b.fileName));
    const totalItems = sortedChildren.length + sortedFiles.length;

    // Render tree guide lines based on depth
    const renderGuides = (itemIndex: number, total: number) => {
      const guides = [];
      for (let i = 0; i < depth; i++) {
        guides.push(<div key={i} className={styles.treeGuide} />);
      }
      // Add the connector guide for this item
      if (depth > 0) {
        guides.push(
          <div
            key="connector"
            className={`${styles.treeGuide} ${itemIndex === total - 1 ? styles.isLast : ''}`}
          />,
        );
      }
      return guides;
    };

    return (
      <div key={folder.path}>
        {folder.path !== '/' && (
          <div className={styles.treeItemWrapper}>
            {renderGuides(0, 1)}
            <div className={styles.treeItemContent}>
              <ContextMenu>
                <ContextMenuTrigger>
                  <div
                    className={styles.folderItem}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFolder(folder.path);
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleFolder(folder.path);
                      }
                    }}
                  >
                    <ChevronIcon size={12} className={styles.chevron} />
                    <FolderIcon size={14} className={styles.folderIcon} />
                    <span className={styles.folderName}>{folder.name}</span>
                  </div>
                </ContextMenuTrigger>
                <ContextMenuContent>
                  <ContextMenuItem onSelect={() => handleCreateFileInFolder(folder.path)}>
                    <FilePlus size={14} style={{ marginRight: '8px' }} />
                    New File
                  </ContextMenuItem>
                  <ContextMenuItem onSelect={() => handleCreateFolder(folder.path)}>
                    <FolderPlus size={14} style={{ marginRight: '8px' }} />
                    New Folder
                  </ContextMenuItem>
                  <ContextMenuSeparator />
                  <ContextMenuItem onSelect={() => handleDeleteFolder(folder.path)}>
                    <Trash2 size={14} style={{ marginRight: '8px' }} />
                    Delete
                  </ContextMenuItem>
                </ContextMenuContent>
              </ContextMenu>
            </div>
          </div>
        )}

        {isExpanded && (
          <div className={styles.folderContent}>
            {/* Render subfolders */}
            {sortedChildren.map((child, idx) =>
              renderFolder(
                child,
                depth + 1,
                idx === sortedChildren.length - 1 && sortedFiles.length === 0,
              ),
            )}

            {/* Render files in this folder */}
            {sortedFiles.map((file, idx) => {
              // Normalize path for lookup
              const normalizePath = (path: string) => {
                if (!path || path === '/') return '/';
                return path.replace(/\/+/g, '/').replace(/\/$/, '') || '/';
              };

              const filePath = normalizePath(file.filePath || '/');
              const fileKey = `${filePath}/${file.fileName}`;

              // Try multiple key formats
              const status =
                fileStatusMap.get(fileKey) ||
                fileStatusMap.get(`${filePath}${file.fileName}`) ||
                fileStatusMap.get(`/${file.fileName}`) ||
                fileStatusMap.get(file.fileName) ||
                'unchanged';

              const isLastFile = idx === sortedFiles.length - 1;

              return (
                <div key={file.id} className={styles.treeItemWrapper}>
                  {/* Render guide lines for each depth level */}
                  {Array.from({ length: depth + 1 }).map((_, i) => (
                    <div
                      key={i}
                      className={`${styles.treeGuide} ${i === depth && isLastFile ? styles.isLast : ''}`}
                    />
                  ))}
                  <div
                    className={`${styles.treeItemContent} ${folder.path !== '/' ? styles.nestedFileContent : ''}`}
                  >
                    <FileTreeItem
                      file={file}
                      isActive={activeFileId === file.id}
                      onSelect={onSelectFile}
                      onDelete={handleDelete}
                      onRename={handleRename}
                      onDuplicate={handleDuplicate}
                      status={status}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        {onCollapse && (
          <div className={styles.headerTop}>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={onCollapse}
              title="Collapse File Explorer"
            >
              <PanelLeftClose size={14} />
            </Button>
          </div>
        )}
        <div className={styles.headerBottom}>
          <h3 className={styles.title}>Files</h3>
          <div className={styles.actions}>
            {isOwner && safeSessionId && (
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={handleCleanupDuplicates}
                title="Remove duplicate files"
                disabled={isCleaningUp}
              >
                <Eraser size={14} className={isCleaningUp ? styles.spinning : ''} />
              </Button>
            )}
            {onImport && (
              <Button variant="ghost" size="icon-sm" onClick={onImport} title="Import Files">
                <Upload size={14} />
              </Button>
            )}
            <Button variant="ghost" size="icon-sm" onClick={onAddFile} title="New File">
              <FilePlus size={14} />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => handleCreateFolder('/')}
              title="New Folder"
            >
              <FolderPlus size={14} />
            </Button>
          </div>
        </div>
      </div>

      <div className={styles.list}>
        {files.length === 0 ? (
          <div className={styles.empty}>
            <p>No files yet.</p>
            <Button variant="link" onClick={onAddFile} className={styles.emptyBtn}>
              Create one
            </Button>
          </div>
        ) : (
          renderFolder(folderTree)
        )}
      </div>

      {createFileInFolder !== null && (
        <CreateFileDialog
          sessionId={safeSessionId}
          open={createFileInFolder !== null}
          onOpenChange={(open) => {
            if (!open) setCreateFileInFolder(null);
          }}
          defaultPath={createFileInFolder || '/'}
        />
      )}
    </div>
  );
};
